import React, { useState } from "react";
import logo from "../assets/taskit_logo.png";
import "./LandingPage.css";

const CAPABILITY_GROUPS = [
  {
    name: "Plan",
    color: "#7c3aed",
    icon: "◇",
    items: ["Project requirements assistant", "Work breakdown structure", "Gantt chart scheduling"]
  },
  {
    name: "Control",
    color: "#2563eb",
    icon: "▤",
    items: ["Tickets control assistant", "Team control assistant", "Multi-workstream support"]
  },
  {
    name: "Collaborate",
    color: "#0f766e",
    icon: "◈",
    items: ["Team chat", "Live meetings", "Stakeholder presentations"]
  },
  {
    name: "Build",
    color: "#be185d",
    icon: "▲",
    items: ["AI agents that write code", "Jira and GitHub connections", "Bring your own AI key"]
  }
];

const WORKFLOW = [
  {
    step: "1",
    title: "Plan",
    copy: "Describe the initiative. Taskit drafts the requirements, breaks them into a WBS, and lays out the schedule."
  },
  {
    step: "2",
    title: "Control",
    copy: "Track the board, assign the team, and follow progress across every workstream in one timeline."
  },
  {
    step: "3",
    title: "Deploy",
    copy: "Publish to Jira, connect the GitHub repository, and let AI agents pick up the implementation."
  }
];

const INTEGRATIONS = [
  "Jira",
  "GitHub",
  "Bring your own AI key",
  "Model agnostic",
  "Live meetings",
  "Presentation and Excel exports"
];

// Illustrative timeline for the hero. Every epic, engineer and date here is fictional.
const TIMELINE_ROWS = [
  { epic: "Onboarding · Guided workspace setup", dev: "Maya Rodriguez", meta: "Done · 100% · 56h", start: 0, span: 1, state: "done" },
  { epic: "Reporting · Metrics service foundation", dev: "Liam Carter", meta: "Done · 100% · 48h", start: 0, span: 2, state: "done" },
  { epic: "Reporting · Dashboard layout engine", dev: "Maya Rodriguez", meta: "Done · 100% · 48h", start: 1, span: 2, state: "done" },
  { epic: "Billing · Usage limits for production", dev: "Liam Carter", meta: "Late · 82% · 104h", start: 2, span: 3, state: "late" },
  { epic: "Platform · Reliability and failure handling", dev: "Noah Bennett", meta: "Late · 0% · 24h", start: 4, span: 1, state: "risk" }
];

const TIMELINE_WEEKS = [
  { label: "W1", date: "2 Mar" },
  { label: "W2", date: "9 Mar" },
  { label: "W3", date: "16 Mar" },
  { label: "W4", date: "23 Mar" },
  { label: "W5", date: "30 Mar" },
  { label: "W6", date: "6 Apr" }
];

// The "today" marker runs the full height of the chart, as it does in the real Gantt Studio.
const TODAY_WEEK = 1;
const TODAY_OFFSET = 35;

const TEAM_AVATARS = [
  { initials: "MR", color: "#4f46e5" },
  { initials: "LC", color: "#0f766e" },
  { initials: "SA", color: "#b45309" },
  { initials: "NB", color: "#be185d" }
];

const GanttPreview = () => (
  <div className="lp-shot" aria-hidden="true">
    <div className="lp-shot-head">
      <div>
        <h3>Gantt Studio</h3>
        <p>Consolidated timeline from every workstream.</p>
      </div>
      <div className="lp-shot-actions">
        <i style={{ background: "#0f766e" }} />
        <i style={{ background: "#1e293b" }} />
        <i style={{ background: "#4f46e5" }} />
      </div>
    </div>

    <div className="lp-shot-panel">
      <div className="lp-shot-top">
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span className="lp-shot-select">All workstreams</span>
          <span className="lp-shot-avatars">
            {TEAM_AVATARS.map((avatar) => (
              <span key={avatar.initials} style={{ background: avatar.color }}>{avatar.initials}</span>
            ))}
          </span>
        </div>
        <span className="lp-shot-saved">Latest version saved</span>
      </div>

      <div className="lp-shot-grid">
        <div className="lp-shot-h lp-shot-h--first">Epic / developer / progress</div>
        {TIMELINE_WEEKS.map((week) => (
          <div key={week.label} className="lp-shot-h">
            {week.label}
            <small>{week.date}</small>
          </div>
        ))}

        {TIMELINE_ROWS.map((row) => (
          <React.Fragment key={row.epic}>
            <div className="lp-shot-task">
              <b>{row.epic}</b>
              <em>Developer: {row.dev}</em>
              <small>{row.meta}</small>
            </div>
            {TIMELINE_WEEKS.map((week, index) => (
              <div key={week.label} className="lp-shot-cell">
                {index === row.start && (
                  <span
                    className={`lp-shot-bar lp-shot-bar--${row.state}`}
                    style={{ left: "8%", width: `${row.span * 100 - 16}%` }}
                  >
                    {row.epic}
                  </span>
                )}
                {index === TODAY_WEEK && (
                  <span className="lp-shot-today" style={{ left: `${TODAY_OFFSET}%` }} />
                )}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
);

const LandingPage = ({ onStart, onStartPro, onLogin }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="lp">
      <section className="lp-hero">
        <div className="lp-shell">
          <nav className="lp-nav">
            <div className="lp-brand">
              <img src={logo} alt="Taskit logo" />
              <div>
                <strong>Taskit</strong>
                <span>AI project management platform</span>
              </div>
            </div>

            <button
              className="lp-nav-toggle"
              aria-label="Toggle navigation"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              ☰
            </button>

            <div className={`lp-nav-links${menuOpen ? " is-open" : ""}`}>
              <button type="button" className="lp-nav-link" onClick={() => scrollTo("platform")}>Platform</button>
              <button type="button" className="lp-nav-link" onClick={() => scrollTo("integrations")}>Integrations</button>
              <button type="button" className="lp-nav-link" onClick={() => scrollTo("pricing")}>Pricing</button>
              <button className="lp-btn lp-btn-secondary" onClick={onLogin}>Log in</button>
              <button className="lp-btn lp-btn-primary" onClick={onStart}>Start free</button>
            </div>
          </nav>

          <div className="lp-hero-grid">
            <div>
              <span className="lp-eyebrow">AI project management for product managers</span>
              <h1 className="lp-h1">
                Plan, control, and deploy projects <span className="lp-grad">with AI at every step</span>
              </h1>
              <p className="lp-lead">
                From raw requirements to a shipped release. Requirements, work breakdown, Gantt,
                board, team, meetings, and code agents — in one workspace, connected to the tools
                you already use.
              </p>
              <div className="lp-cta-row">
                <button className="lp-btn lp-btn-primary" onClick={onStart}>Start free →</button>
                <button className="lp-btn lp-btn-secondary" onClick={onStartPro}>Start with Pro · $29/mo</button>
              </div>
              <div className="lp-proof">
                <span>13 assistants</span>
                <span>Jira and GitHub native</span>
                <span>Bring your own AI key</span>
              </div>
            </div>

            <GanttPreview />
          </div>
        </div>
      </section>

      <section className="lp-section" id="platform">
        <h2 className="lp-section-title">Everything a project manager runs, in one place</h2>
        <p className="lp-section-sub">
          Thirteen assistants across four stages of delivery — not a ticket generator.
        </p>

        <div className="lp-groups">
          {CAPABILITY_GROUPS.map((group) => (
            <div className="lp-group" key={group.name}>
              <div className="lp-group-icon" style={{ background: group.color }}>{group.icon}</div>
              <h3>{group.name}</h3>
              <ul>
                {group.items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-section">
        <h2 className="lp-section-title">One thread, from idea to release</h2>
        <p className="lp-section-sub">The same workspace carries the project through every stage.</p>

        <div className="lp-flow">
          {WORKFLOW.map((stage) => (
            <div className="lp-flow-card" key={stage.title}>
              <span className="lp-step">{stage.step}</span>
              <h3>{stage.title}</h3>
              <p>{stage.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-section" id="integrations">
        <h2 className="lp-section-title">Fits the stack you already use</h2>
        <p className="lp-section-sub">
          Taskit connects to your tools and runs on your own AI key, so your data and your model
          choice stay yours.
        </p>

        <div className="lp-pills">
          {INTEGRATIONS.map((item) => <span className="lp-pill" key={item}>{item}</span>)}
        </div>
      </section>

      <section className="lp-pricing" id="pricing">
        <h2 className="lp-section-title">Simple, transparent pricing</h2>
        <p className="lp-section-sub">Two plans. Bring your own AI key on both.</p>

        <div className="lp-price-grid">
          <div className="lp-price-card">
            <h3>Free</h3>
            <p className="lp-price">$0 <span>/ month</span></p>
            <ul>
              <li>✓ 1 workstream</li>
              <li>✓ 0 invited users</li>
              <li>✓ Unlimited Team Studio profiles</li>
              <li>✓ Every studio and assistant</li>
              <li>✓ Bring your own AI key</li>
            </ul>
            <button className="lp-btn lp-btn-light" onClick={onStart}>Start free</button>
          </div>

          <div className="lp-price-card lp-price-card--pro">
            <span className="lp-badge">Most popular</span>
            <h3>Pro</h3>
            <p className="lp-price">$29 <span>/ month</span></p>
            <ul>
              <li>✓ Up to 10 workstreams</li>
              <li>✓ Up to 10 invited users</li>
              <li>✓ Unlimited Team Studio profiles</li>
              <li>✓ Every studio and assistant</li>
              <li>✓ Bring your own AI key</li>
            </ul>
            <button className="lp-btn lp-btn-primary" onClick={onStartPro}>Start with Pro</button>
          </div>
        </div>
      </section>

      <section className="lp-final">
        <h2>Stop formatting. Start delivering.</h2>
        <button className="lp-btn lp-btn-primary" onClick={onStart}>Start free →</button>
      </section>

      <footer className="lp-footer">
        <strong>Taskit</strong>
        <p>AI project management platform</p>
        <p style={{ color: "#64748b" }}>© 2026 Taskit. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LandingPage;
